import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Pod24AdminLayout } from "../../components/Pod24Admin/Pod24AdminLayout";
import { adminUserApiService } from "infrastructure/api/admin/admin-user-api-service";
import type { WithdrawalPendingUser } from "domain/types/withdrawal";
import type {
  AdminGeneralUser,
  AdminGeneralUserSummary,
  AdminManagerUser,
  ManagerRole,
  RequestStatus,
} from "domain/types/admin-user";

// ───────────────────────────────────────────────────────────────────────────
// helpers
// ───────────────────────────────────────────────────────────────────────────

type TabKey = "manager" | "user" | "pending";

const ROLE_BADGE: Record<ManagerRole, { bg: string; color: string }> = {
  COMPANY_ADMIN: { bg: "#E6F4EA", color: "#1F7A3F" },
  COMPANY_MANAGER: { bg: "#EAEAFA", color: "#5A4FCF" },
};

const FONT_FAMILY = "Pretendard, -apple-system, BlinkMacSystemFont, sans-serif";

const PAGE_SIZE = 50;

/** 사용자 식별을 위한 색상 풀 (회사 목록의 LogoMini 와 동일 컨셉). */
const AVATAR_PALETTE = [
  "#4285F4",
  "#27AE60",
  "#5A4FCF",
  "#B26A00",
  "#C53030",
  "#1170BF",
  "#A12A6F",
  "#1F7A3F",
  "#1A4FA5",
];

const pickAvatarColor = (seed: number): string =>
  AVATAR_PALETTE[Math.abs(seed) % AVATAR_PALETTE.length];

/** 회사명 → 2글자 약어. (백엔드에 로고 코드가 없으므로 클라이언트에서 생성) */
const buildLogoCode = (name: string | null | undefined): string => {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return "??";
  if (/^[A-Za-z0-9]/.test(trimmed)) {
    return trimmed.slice(0, 2).toUpperCase();
  }
  return trimmed.charAt(0);
};

/** "yyyy-MM-ddTHH:mm:ss[...]" → "yyyy. MM. DD." */
const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[1]}. ${match[2]}. ${match[3]}.`;
  return value;
};

// ───────────────────────────────────────────────────────────────────────────
// shared sub-components
// ───────────────────────────────────────────────────────────────────────────

const Avatar: React.FC<{ char: string; color: string }> = ({ char, color }) => (
  <span
    className="rounded-full inline-flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0"
    style={{ width: 28, height: 28, background: color }}
  >
    {char}
  </span>
);

const LogoMini: React.FC<{ code: string }> = ({ code }) => (
  <span
    className="rounded-[5px] inline-flex items-center justify-center text-[9px] font-bold flex-shrink-0"
    style={{ width: 22, height: 22, background: "#F0F1F4", color: "#555" }}
  >
    {code}
  </span>
);

const RoleBadge: React.FC<{ role: ManagerRole }> = ({ role }) => {
  const { bg, color } = ROLE_BADGE[role];
  return (
    <span
      className="inline-flex items-center rounded-[4px] text-[11px] font-medium"
      style={{ padding: "2px 8px", background: bg, color }}
    >
      {role}
    </span>
  );
};

/** 화면에는 개발용 enum 이름 대신 사람이 읽는 말로 보여 준다. */
const ROLE_LABEL: Record<string, string> = {
  ADMIN: "POD24 관리자",
  COMPANY_ADMIN: "회사 관리자",
  COMPANY_MANAGER: "회사 매니저",
  USER: "일반 회원",
};

const thClass =
  "bg-[#F8F8F8] text-[#555] font-medium text-[12.5px] py-3 px-[18px] text-left border-b border-[#D9D9D9]";
const tdClass = "py-3 px-[18px] text-[13px] border-b border-[#D9D9D9]";

const inputClass =
  "w-full border border-[#D9D9D9] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#4285F4] bg-white";
const resetBtnClass =
  "inline-flex items-center justify-center font-medium rounded-lg px-4 py-2 text-[13px] bg-white text-[#374151] border border-[#D1D5DB] hover:bg-[#F9FAFB] min-w-[85px]";

// ───────────────────────────────────────────────────────────────────────────
// main page
// ───────────────────────────────────────────────────────────────────────────

const Users: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>("manager");

  // ───── 거래 업체 관리자 탭 상태 ─────
  const [managers, setManagers] = useState<AdminManagerUser[]>([]);
  const [managerTotal, setManagerTotal] = useState(0);
  const [managerPage, setManagerPage] = useState(0);
  const [managerLoading, setManagerLoading] = useState(true);
  const [managerError, setManagerError] = useState<string | null>(null);

  const [managerKeyword, setManagerKeyword] = useState("");
  const [roleFilter, setRoleFilter] = useState<"" | ManagerRole>("");
  const [companyFilter, setCompanyFilter] = useState("");

  const managerSentinelRef = useRef<HTMLDivElement | null>(null);
  const managerHasMore = managers.length < managerTotal;

  // 거래 업체 관리자 페이지 로드 (page 0 = reset, page 1+ = append)
  useEffect(() => {
    let cancelled = false;
    const fetchPage = async (): Promise<void> => {
      setManagerLoading(true);
      setManagerError(null);
      try {
        const res = await adminUserApiService.listManagers({
          page: managerPage,
          size: PAGE_SIZE,
        });
        if (cancelled) return;
        const data = res.data;
        const items = data?.items ?? [];
        setManagers((prev) => (managerPage === 0 ? items : [...prev, ...items]));
        setManagerTotal(data?.totalElements ?? 0);
      } catch (err) {
        if (cancelled) return;
        setManagerError(
          err instanceof Error
            ? err.message
            : "거래 업체 관리자 목록을 불러오지 못했습니다.",
        );
      } finally {
        if (!cancelled) setManagerLoading(false);
      }
    };
    void fetchPage();
    return () => {
      cancelled = true;
    };
  }, [managerPage]);

  // 무한 스크롤 — 거래 업체 관리자 탭
  useEffect(() => {
    if (activeTab !== "manager") return;
    const sentinel = managerSentinelRef.current;
    if (!sentinel || !managerHasMore || managerLoading) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setManagerPage((p) => p + 1);
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [activeTab, managerHasMore, managerLoading]);

  // 현재까지 누적된 회사 목록 — 필터용 (가나다순 보장은 백엔드 default 정렬에 위임)
  const managerCompanyOptions = useMemo(() => {
    const set = new Set<string>();
    managers.forEach((m) => {
      if (m.companyName) set.add(m.companyName);
    });
    return Array.from(set);
  }, [managers]);

  const filteredManagers = useMemo(() => {
    const keyword = managerKeyword.trim().toLowerCase();
    return managers.filter((m) => {
      if (keyword) {
        const matched =
          m.name.toLowerCase().includes(keyword) ||
          m.email.toLowerCase().includes(keyword) ||
          (m.phone ?? "").includes(keyword);
        if (!matched) return false;
      }
      if (roleFilter && m.role !== roleFilter) return false;
      if (companyFilter && m.companyName !== companyFilter) return false;
      return true;
    });
  }, [managers, managerKeyword, roleFilter, companyFilter]);

  const handleManagerReset = (): void => {
    setManagerKeyword("");
    setRoleFilter("");
    setCompanyFilter("");
  };

  // ───── 탈퇴 유예중 탭 상태 ─────
  // 유예가 끝나면 스케줄러가 지우고 그 뒤에는 복구할 수 없어, 남은 기간을 강조해 보여준다.
  const [pendingUsers, setPendingUsers] = useState<WithdrawalPendingUser[]>([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingError, setPendingError] = useState<string | null>(null);
  const [pendingKeyword, setPendingKeyword] = useState("");
  const [recoverTarget, setRecoverTarget] = useState<WithdrawalPendingUser | null>(null);

  const loadPendingUsers = useCallback(async (): Promise<void> => {
    setPendingLoading(true);
    setPendingError(null);

    try {
      const res = await adminUserApiService.listWithdrawalPendingUsers({
        page: 0,
        size: PAGE_SIZE,
        keyword: pendingKeyword.trim() || null,
      });
      setPendingUsers(res.data?.items ?? []);
    } catch (e) {
      setPendingError(
        e instanceof Error ? e.message : "탈퇴 유예중 회원을 가져오지 못했습니다.",
      );
      setPendingUsers([]);
    } finally {
      setPendingLoading(false);
    }
  }, [pendingKeyword]);

  useEffect(() => {
    if (activeTab !== "pending") return;
    loadPendingUsers();
  }, [activeTab, loadPendingUsers]);

  const recover = async (): Promise<void> => {
    if (!recoverTarget) return;

    try {
      await adminUserApiService.recoverUser(recoverTarget.userId);
      setRecoverTarget(null);
      loadPendingUsers();
    } catch (e) {
      setPendingError(e instanceof Error ? e.message : "복구에 실패했습니다.");
      setRecoverTarget(null);
    }
  };

  // ───── (POD24) 일반 사용자 탭 상태 ─────
  const [generalUsers, setGeneralUsers] = useState<AdminGeneralUser[]>([]);
  const [generalTotal, setGeneralTotal] = useState(0);
  const [generalPage, setGeneralPage] = useState(0);
  const [generalLoading, setGeneralLoading] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [generalSummary, setGeneralSummary] =
    useState<AdminGeneralUserSummary | null>(null);

  const [userKeyword, setUserKeyword] = useState("");
  const [requestFilter, setRequestFilter] = useState<"" | RequestStatus>("");
  const [joinedFilter, setJoinedFilter] = useState("");

  const [generalLoaded, setGeneralLoaded] = useState(false);
  const generalSentinelRef = useRef<HTMLDivElement | null>(null);
  const generalHasMore = generalUsers.length < generalTotal;

  // 일반 사용자 탭 진입 시 1회 — 요약 카드 4종 + 첫 페이지 로드
  useEffect(() => {
    if (activeTab !== "user" || generalLoaded) return;
    setGeneralLoaded(true);
    setGeneralPage(0);

    // cancelled 체크 제거 — setGeneralLoaded(true) 가 의존성을 바꿔 cleanup 이 즉시 발동되어
    // 응답이 무시되던 race condition. summary 는 탭 진입 시 1회성 호출이라 무조건 set.
    const fetchSummary = async (): Promise<void> => {
      try {
        const res = await adminUserApiService.getGeneralUserSummary();
        if (res.data) setGeneralSummary(res.data);
      } catch {
        // 요약은 실패해도 본문 로드는 진행
      }
    };
    void fetchSummary();
  }, [activeTab, generalLoaded]);

  // 일반 사용자 페이지 로드
  useEffect(() => {
    if (!generalLoaded) return;
    let cancelled = false;
    const fetchPage = async (): Promise<void> => {
      setGeneralLoading(true);
      setGeneralError(null);
      try {
        const res = await adminUserApiService.listGeneralUsers({
          page: generalPage,
          size: PAGE_SIZE,
        });
        if (cancelled) return;
        const data = res.data;
        const items = data?.items ?? [];
        setGeneralUsers((prev) =>
          generalPage === 0 ? items : [...prev, ...items],
        );
        setGeneralTotal(data?.totalElements ?? 0);
      } catch (err) {
        if (cancelled) return;
        setGeneralError(
          err instanceof Error
            ? err.message
            : "일반 사용자 목록을 불러오지 못했습니다.",
        );
      } finally {
        if (!cancelled) setGeneralLoading(false);
      }
    };
    void fetchPage();
    return () => {
      cancelled = true;
    };
  }, [generalLoaded, generalPage]);

  // 무한 스크롤 — 일반 사용자 탭
  useEffect(() => {
    if (activeTab !== "user") return;
    const sentinel = generalSentinelRef.current;
    if (!sentinel || !generalHasMore || generalLoading) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setGeneralPage((p) => p + 1);
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [activeTab, generalHasMore, generalLoading]);

  const filteredGeneralUsers = useMemo(() => {
    const keyword = userKeyword.trim().toLowerCase();
    const now = Date.now();
    const joinedDays = joinedFilter ? Number(joinedFilter) : null;
    return generalUsers.filter((u) => {
      if (keyword) {
        const matched =
          u.name.toLowerCase().includes(keyword) ||
          u.email.toLowerCase().includes(keyword) ||
          (u.phone ?? "").includes(keyword);
        if (!matched) return false;
      }
      if (requestFilter && u.requestStatus !== requestFilter) return false;
      if (joinedDays && joinedDays > 0) {
        const joinedAt = new Date(u.joinedAt).getTime();
        if (!Number.isFinite(joinedAt)) return false;
        const daysAgo = (now - joinedAt) / (1000 * 60 * 60 * 24);
        if (daysAgo > joinedDays) return false;
      }
      return true;
    });
  }, [generalUsers, userKeyword, requestFilter, joinedFilter]);

  const handleUserReset = (): void => {
    setUserKeyword("");
    setRequestFilter("");
    setJoinedFilter("");
  };

  return (
    <Pod24AdminLayout>
      <div className="text-[#252525]" style={{ fontFamily: FONT_FAMILY }}>
        {/* 페이지 헤더 */}
        <div className="mb-6">
          <h1 className="text-[22px] font-bold text-[#222]">사용자 관리</h1>
          <p className="text-[13px] text-[#888] mt-1">
            POD24에 가입한 사용자 중 거래 업체 관리자, 회사 미소속 일반
            사용자를 조회합니다.
          </p>
        </div>

        {/* 탭 */}
        <div className="flex items-center gap-7 border-b border-[#D9D9D9] mb-4">
          <button
            onClick={() => setActiveTab("manager")}
            className={`py-3 text-[14px] ${
              activeTab === "manager"
                ? "text-[#252525] font-semibold border-b-2 border-[#4285F4]"
                : "text-[#888]"
            }`}
          >
            거래 업체 관리자
          </button>
          <button
            onClick={() => setActiveTab("user")}
            className={`py-3 text-[14px] ${
              activeTab === "user"
                ? "text-[#252525] font-semibold border-b-2 border-[#4285F4]"
                : "text-[#888]"
            }`}
          >
            (POD24) 일반 사용자
          </button>
          <button
            onClick={() => setActiveTab("pending")}
            className={`py-3 text-[14px] ${
              activeTab === "pending"
                ? "text-[#252525] font-semibold border-b-2 border-[#4285F4]"
                : "text-[#888]"
            }`}
          >
            탈퇴 유예중
          </button>
        </div>

        {/* ============ 거래 업체 관리자 탭 ============ */}
        {activeTab === "manager" && (
          <div>
            {managerError && (
              <div className="mb-4 p-3 rounded-lg border border-[#4285F4]/20 bg-[#EBF3FF] text-[13px] text-[#4285F4]">
                {managerError}
              </div>
            )}

            {/* 검색/필터 박스 */}
            <div className="mb-5 p-5 border border-[#D9D9D9] rounded-[10px] bg-white flex flex-col md:flex-row gap-3">
              <input
                type="text"
                value={managerKeyword}
                onChange={(e) => setManagerKeyword(e.target.value)}
                className="flex-1 border border-[#D9D9D9] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#4285F4] bg-white"
                placeholder="이름 / 이메일 / 연락처로 검색"
              />
              <select
                value={roleFilter}
                onChange={(e) =>
                  setRoleFilter(e.target.value as "" | ManagerRole)
                }
                className={`${inputClass} md:w-[180px]`}
              >
                <option value="">전체 권한</option>
                <option value="COMPANY_ADMIN">COMPANY_ADMIN</option>
                <option value="COMPANY_MANAGER">COMPANY_MANAGER</option>
              </select>
              <select
                value={companyFilter}
                onChange={(e) => setCompanyFilter(e.target.value)}
                className={`${inputClass} md:w-[180px]`}
              >
                <option value="">전체 회사</option>
                {managerCompanyOptions.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <button onClick={handleManagerReset} className={resetBtnClass}>
                초기화
              </button>
            </div>

            {/* 테이블 */}
            <div className="rounded-lg overflow-x-auto border border-[#D9D9D9] bg-white mb-4">
              <table
                className="w-full"
                style={{ borderCollapse: "separate", borderSpacing: 0 }}
              >
                <thead>
                  <tr>
                    <th className={thClass}>이름</th>
                    <th className={`${thClass} w-[130px]`}>연락처</th>
                    <th className={`${thClass} w-[180px]`}>본사</th>
                    <th className={`${thClass} w-[170px]`}>권한</th>
                    <th className={`${thClass} w-[200px]`}>이메일</th>
                    <th className={`${thClass} w-[110px]`}>가입일</th>
                    <th className={`${thClass} w-[130px]`}>최근 접속일</th>
                  </tr>
                </thead>
                <tbody>
                  {managerLoading && managers.length === 0 && (
                    <tr>
                      <td
                        colSpan={7}
                        className="py-8 px-[18px] text-[13px] text-center text-[#888]"
                      >
                        불러오는 중...
                      </td>
                    </tr>
                  )}
                  {filteredManagers.map((m) => (
                    <tr
                      key={m.userId}
                      className="hover:bg-[#F2F2F2] cursor-pointer"
                    >
                      <td className={tdClass}>
                        <div className="flex items-center gap-2.5">
                          <Avatar
                            char={m.name.charAt(0) || "?"}
                            color={pickAvatarColor(m.userId)}
                          />
                          <span className="font-medium">{m.name}</span>
                        </div>
                      </td>
                      <td className={`${tdClass} text-[#555]`}>
                        {m.phone || "—"}
                      </td>
                      <td className={tdClass}>
                        <div className="flex items-center gap-2">
                          <LogoMini code={buildLogoCode(m.companyName)} />
                          <span>{m.companyName ?? "—"}</span>
                        </div>
                      </td>
                      <td className={tdClass}>
                        <RoleBadge role={m.role} />
                      </td>
                      <td className={tdClass}>
                        <a
                          href={`mailto:${m.email}`}
                          className="text-[#4285F4] hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {m.email}
                        </a>
                      </td>
                      <td className={`${tdClass} text-[#757575]`}>
                        {formatDate(m.joinedAt)}
                      </td>
                      <td className={`${tdClass} text-[#757575]`}>
                        {formatDate(m.lastAccessedAt)}
                      </td>
                    </tr>
                  ))}
                  {!managerLoading &&
                    managers.length > 0 &&
                    filteredManagers.length === 0 && (
                      <tr>
                        <td
                          colSpan={7}
                          className="py-8 px-[18px] text-[13px] text-center text-[#888]"
                        >
                          검색 조건에 해당하는 사용자가 없습니다.
                        </td>
                      </tr>
                    )}
                  {!managerLoading &&
                    managers.length === 0 &&
                    !managerError && (
                      <tr>
                        <td
                          colSpan={7}
                          className="py-8 px-[18px] text-[13px] text-center text-[#888]"
                        >
                          등록된 거래 업체 관리자가 없습니다.
                        </td>
                      </tr>
                    )}
                </tbody>
              </table>
            </div>

            {/* 무한 스크롤 sentinel */}
            <div className="flex items-center justify-between text-[12px] mb-4">
              <div className="text-[#757575]">
                거래 업체 관리자{" "}
                <span className="text-[#252525] font-medium">
                  {managerTotal}
                </span>{" "}
                명
              </div>
            </div>
            {managerHasMore && (
              <div
                ref={managerSentinelRef}
                className="text-center text-[12px] text-[#888] py-4 mb-4"
              >
                {managerLoading ? "불러오는 중..." : "스크롤하면 더 불러옵니다"}
              </div>
            )}
            {!managerHasMore && managers.length > 0 && (
              <div className="text-center text-[12px] text-[#888] py-3 mb-4">
                전체 {managerTotal} 명을 모두 불러왔습니다
              </div>
            )}
          </div>
        )}

        {/* ============ (POD24) 일반 사용자 탭 ============ */}
        {activeTab === "user" && (
          <div>
            {generalError && (
              <div className="mb-4 p-3 rounded-lg border border-[#4285F4]/20 bg-[#EBF3FF] text-[13px] text-[#4285F4]">
                {generalError}
              </div>
            )}

            {/* 요청 상태 요약 카드 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="border border-[#D9D9D9] rounded-md p-3 bg-white">
                <div className="text-[11px] text-[#888] mb-1">
                  전체 일반 사용자
                </div>
                <div className="text-[16px] font-semibold">
                  {generalSummary?.total ?? "—"} 명
                </div>
              </div>
              <div className="border border-[#D9D9D9] rounded-md p-3 bg-white">
                <div className="text-[11px] text-[#888] mb-1">
                  회사 등록 요청 없음
                </div>
                <div className="text-[16px] font-semibold text-[#555]">
                  {generalSummary?.noRequest ?? "—"} 명
                </div>
              </div>
              <a
                href="/admin/company/requests"
                className="border border-[#B26A00]/30 rounded-md p-3 bg-[#FFF8EE] block hover:bg-[#FFF1D9] transition-colors"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] text-[#B26A00]">승인 대기 중</span>
                  <span className="text-[10.5px] text-[#B26A00]">
                    신규 업체 등록 ›
                  </span>
                </div>
                <div className="text-[16px] font-semibold text-[#B26A00]">
                  {generalSummary?.pending ?? "—"} 건
                </div>
              </a>
              <div className="border border-[#D9D9D9] rounded-md p-3 bg-white">
                <div className="text-[11px] text-[#888] mb-1">
                  최근 30일 신규 가입
                </div>
                <div className="text-[16px] font-semibold text-[#555]">
                  {generalSummary?.recent30Days ?? "—"} 명
                </div>
              </div>
            </div>

            {/* 검색/필터 박스 */}
            <div className="mb-5 p-5 border border-[#D9D9D9] rounded-[10px] bg-white flex flex-col md:flex-row gap-3">
              <input
                type="text"
                value={userKeyword}
                onChange={(e) => setUserKeyword(e.target.value)}
                className="flex-1 border border-[#D9D9D9] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#4285F4] bg-white"
                placeholder="이름 / 이메일 / 연락처로 검색"
              />
              <select
                value={requestFilter}
                onChange={(e) =>
                  setRequestFilter(e.target.value as "" | RequestStatus)
                }
                className={`${inputClass} md:w-[200px]`}
              >
                <option value="">회사 등록 요청 — 전체</option>
                <option value="NONE">요청 없음</option>
                <option value="PENDING">승인 대기 중</option>
              </select>
              <select
                value={joinedFilter}
                onChange={(e) => setJoinedFilter(e.target.value)}
                className={`${inputClass} md:w-[160px]`}
              >
                <option value="">가입일 — 전체</option>
                <option value="7">최근 7일</option>
                <option value="30">최근 30일</option>
                <option value="90">최근 90일</option>
              </select>
              <button onClick={handleUserReset} className={resetBtnClass}>
                초기화
              </button>
            </div>

            {/* 테이블 */}
            <div className="rounded-lg overflow-x-auto border border-[#D9D9D9] bg-white mb-4">
              <table
                className="w-full"
                style={{ borderCollapse: "separate", borderSpacing: 0 }}
              >
                <thead>
                  <tr>
                    <th className={thClass}>이름</th>
                    <th className={`${thClass} w-[130px]`}>연락처</th>
                    <th className={`${thClass} w-[200px]`}>이메일</th>
                    <th className={`${thClass} w-[110px]`}>가입일</th>
                    <th className={`${thClass} w-[120px]`}>최근 접속일</th>
                    <th className={thClass}>회사 등록 요청</th>
                  </tr>
                </thead>
                <tbody>
                  {generalLoading && generalUsers.length === 0 && (
                    <tr>
                      <td
                        colSpan={6}
                        className="py-8 px-[18px] text-[13px] text-center text-[#888]"
                      >
                        불러오는 중...
                      </td>
                    </tr>
                  )}
                  {filteredGeneralUsers.map((u) => (
                    <tr
                      key={u.userId}
                      className="hover:bg-[#F2F2F2] cursor-pointer"
                    >
                      <td className={tdClass}>
                        <div className="flex items-center gap-2.5">
                          <Avatar
                            char={u.name.charAt(0) || "?"}
                            color={pickAvatarColor(u.userId)}
                          />
                          <span className="font-medium">{u.name}</span>
                        </div>
                      </td>
                      <td className={`${tdClass} text-[#555]`}>
                        {u.phone || "—"}
                      </td>
                      <td className={tdClass}>
                        <a
                          href={`mailto:${u.email}`}
                          className="text-[#4285F4] hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {u.email}
                        </a>
                      </td>
                      <td className={`${tdClass} text-[#757575]`}>
                        {formatDate(u.joinedAt)}
                      </td>
                      <td className={`${tdClass} text-[#757575]`}>
                        {formatDate(u.lastAccessedAt)}
                      </td>
                      <td className={tdClass}>
                        {u.requestStatus === "PENDING" ? (
                          <div className="flex items-center gap-2">
                            <span
                              className="inline-flex items-center rounded-[4px] text-[11px] font-medium"
                              style={{
                                padding: "2px 8px",
                                background: "#FFF4E5",
                                color: "#B26A00",
                              }}
                            >
                              대기 중
                            </span>
                            <span className="text-[12px] text-[#555]">
                              {u.requestedCompanyName ?? ""}
                            </span>
                          </div>
                        ) : (
                          <span
                            className="inline-flex items-center rounded-[4px] text-[11px] font-medium"
                            style={{
                              padding: "2px 8px",
                              background: "#F0F1F4",
                              color: "#555",
                            }}
                          >
                            요청 없음
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!generalLoading &&
                    generalUsers.length > 0 &&
                    filteredGeneralUsers.length === 0 && (
                      <tr>
                        <td
                          colSpan={6}
                          className="py-8 px-[18px] text-[13px] text-center text-[#888]"
                        >
                          검색 조건에 해당하는 사용자가 없습니다.
                        </td>
                      </tr>
                    )}
                  {!generalLoading &&
                    generalUsers.length === 0 &&
                    !generalError && (
                      <tr>
                        <td
                          colSpan={6}
                          className="py-8 px-[18px] text-[13px] text-center text-[#888]"
                        >
                          등록된 일반 사용자가 없습니다.
                        </td>
                      </tr>
                    )}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between text-[12px] mb-4">
              <div className="text-[#757575]">
                일반 사용자{" "}
                <span className="text-[#252525] font-medium">
                  {generalTotal}
                </span>{" "}
                명
              </div>
            </div>
            {generalHasMore && (
              <div
                ref={generalSentinelRef}
                className="text-center text-[12px] text-[#888] py-4 mb-4"
              >
                {generalLoading ? "불러오는 중..." : "스크롤하면 더 불러옵니다"}
              </div>
            )}
            {!generalHasMore && generalUsers.length > 0 && (
              <div className="text-center text-[12px] text-[#888] py-3 mb-4">
                전체 {generalTotal} 명을 모두 불러왔습니다
              </div>
            )}
          </div>
        )}

        {/* ============ 탈퇴 유예중 탭 ============ */}
        {activeTab === "pending" && (
          <div>
            <div className="rounded-lg px-4 py-3.5 mb-4 text-[13px] leading-[1.7] bg-[#EBF3FF] text-[#4285F4] border border-[#4285F4]/20">
              개인 탈퇴를 신청하고 7일 유예 기간 중인 회원입니다. 유예중에는{" "}
              <b className="font-bold">로그인이 차단</b>되고 같은 이메일로 재가입할 수 없습니다.
              본인 요청으로 철회하는 경우 여기서 복구해 주세요. 유예가 끝나 삭제된 뒤에는 복구할 수
              없습니다.
              <br />
              회사 단위 종료는 <b className="font-bold">업체 관리 &gt; 운영 종료 신청</b>에서
              처리합니다.
            </div>

            <div className="flex gap-2 items-center mb-4">
              <div className="w-[280px]">
                <input
                  className={inputClass}
                  placeholder="이름 · 이메일 검색"
                  value={pendingKeyword}
                  onChange={(e) => setPendingKeyword(e.target.value)}
                />
              </div>
            </div>

            {pendingError && (
              <div className="rounded-lg px-4 py-3 mb-4 text-[13px] bg-[#FEF2F2] text-[#F24A4A] border border-[#F24A4A]/20">
                {pendingError}
              </div>
            )}

            <div className="rounded-lg overflow-x-auto border border-[#D9D9D9] bg-white mb-4">
              <table
                className="w-full"
                style={{ borderCollapse: "separate", borderSpacing: 0 }}
              >
                <thead>
                  <tr>
                    <th className={thClass}>이름</th>
                    <th className={`${thClass} w-[200px]`}>이메일</th>
                    <th className={`${thClass} w-[170px]`}>권한</th>
                    <th className={`${thClass} w-[160px]`}>소속</th>
                    <th className={`${thClass} w-[110px]`}>신청일</th>
                    <th className={`${thClass} w-[150px]`}>삭제 예정일</th>
                    <th className={thClass}>사유</th>
                    <th className={`${thClass} w-[100px] text-right`}>처리</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingLoading && (
                    <tr>
                      <td
                        colSpan={8}
                        className="py-8 px-[18px] text-[13px] text-center text-[#888]"
                      >
                        불러오는 중...
                      </td>
                    </tr>
                  )}

                  {!pendingLoading && pendingUsers.length === 0 && !pendingError && (
                    <tr>
                      <td
                        colSpan={8}
                        className="py-8 px-[18px] text-[13px] text-center text-[#888]"
                      >
                        탈퇴 유예중인 회원이 없습니다.
                      </td>
                    </tr>
                  )}

                  {!pendingLoading &&
                    pendingUsers.map((u) => {
                      const remaining = daysUntil(u.scheduledDeletedAt);
                      const urgent = remaining !== null && remaining <= 1;

                      return (
                        <tr key={u.userId} className="hover:bg-[#F2F2F2]">
                          <td className={`${tdClass} font-medium`}>{u.name ?? "—"}</td>
                          <td className={tdClass}>{u.email ?? "—"}</td>
                          <td className={tdClass}>{ROLE_LABEL[u.role] ?? u.role}</td>
                          <td className={tdClass}>
                            {u.companyName ?? (
                              <span className="text-[#bbb]">회사 미소속</span>
                            )}
                          </td>
                          <td className={tdClass}>{formatDate(u.withdrawalRequestedAt)}</td>
                          <td className={tdClass}>
                            <span
                              className={`inline-flex items-center rounded-[4px] text-[11px] font-medium px-2 py-0.5 ${
                                urgent
                                  ? "bg-[#FEF2F2] text-[#F24A4A]"
                                  : "bg-[#E8EFFF] text-[#4285F4]"
                              }`}
                            >
                              {remaining === null ? "—" : `D-${remaining}`} ·{" "}
                              {formatDate(u.scheduledDeletedAt)}
                            </span>
                          </td>
                          <td className={tdClass}>
                            {u.withdrawalReason ?? (
                              <span className="text-[#bbb]">기록 없음</span>
                            )}
                          </td>
                          <td className={`${tdClass} text-right`}>
                            <button
                              type="button"
                              onClick={() => setRecoverTarget(u)}
                              className="px-3 py-1.5 rounded-md text-[13px] bg-white text-[#4285F4] border border-[#4285F4] hover:bg-[#E8EFFF]"
                            >
                              복구
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>

            <div className="text-[12px] text-[#757575] mb-4">
              유예중 회원{" "}
              <span className="text-[#252525] font-medium">{pendingUsers.length}</span> 명
            </div>
          </div>
        )}
      </div>

      {recoverTarget && (
        <div className="fixed inset-0 z-[6000] flex items-center justify-center p-5">
          <div
            className="absolute inset-0 bg-black opacity-50"
            onClick={() => setRecoverTarget(null)}
          />
          <div className="relative z-[6001] bg-white rounded-[12px] p-6 w-full max-w-[460px]">
            <h3 className="text-[16px] font-bold text-[#222] mb-2.5">
              회원을 복구하시겠습니까?
            </h3>
            <p className="text-[13px] text-[#555] mb-5">
              <b className="font-bold">
                {recoverTarget.name ?? "—"} ({recoverTarget.email ?? "—"})
              </b>{" "}
              님의 탈퇴 신청이 취소되고 계정이 즉시 정상 상태로 돌아갑니다. 로그인과 재가입 제한도
              함께 해제됩니다.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setRecoverTarget(null)}
                className="px-4 py-2 rounded-md text-[13px] border border-[#222] text-[#222] hover:bg-[#f5f5f5]"
              >
                취소
              </button>
              <button
                type="button"
                onClick={recover}
                className="px-4 py-2 rounded-md text-[13px] text-white bg-[#222] hover:bg-[#333]"
              >
                복구하기
              </button>
            </div>
          </div>
        </div>
      )}
    </Pod24AdminLayout>
  );
};

/** 남은 일수. 이미 지났으면 0 */
const daysUntil = (value?: string | null): number | null => {
  if (!value) return null;
  const target = new Date(value).getTime();
  if (Number.isNaN(target)) return null;
  return Math.max(0, Math.ceil((target - Date.now()) / (1000 * 60 * 60 * 24)));
};

export default Users;